import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Tooltip,
    Card,
    CardContent,
    Grid,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useEffect, useState } from 'react';
import { fetchUserSessionDetails } from '../../services/dashboard.service';
import dayjs from 'dayjs';
import NoDataFound from '../common/NoDataFound';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

interface SessionData {
    sessionId: string;
    startTime: string;
    endTime: string | null;
    duration: number;
    isActive: boolean;
    endReason: string | null;
    featureUsage: {
        transcriptions: number;
        transcriptionDurationMinutes: number;
        transcriptionDurationHours: number;
        factFinds: number;
        meetings: number;
        clients: number;
        clientsAssigned: number;
        documents: number;
        aiAgentUsage: number;
        contacts: number;
        fileNotesGenerated: number;
        fileNotesRegenerated: number;
        powerPointsGenerated: number;
        emailsGenerated: number;
        emailsSent: number;
        smsSent: number;
        smsSegments: number;
    };
    timeline: Array<{
        actionName: string;
        timestamp: string;
    }>;
}

interface UserSessionDetailsModalProps {
    open: boolean;
    onClose: () => void;
    userId: string | null;
    userName?: string;
    orgId?: string;
}

const CustomNoRowsOverlay = () => (
    <NoDataFound
        title="No sessions found"
        subtitle="This user has no recorded sessions."
        imageWidth={150}
        imageHeight={150}
    />
);

const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

// Feature badge component with tooltip and click handler
const FeatureBadge = ({
    label,
    value,
    color,
    tooltip,
    onClick
}: {
    label: string;
    value: number | string;
    color: string;
    tooltip?: string;
    onClick?: () => void;
}) => {
    if (!value || value === 0 || value === '0m') return null;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click event
        if (onClick) {
            onClick();
        }
    };

    const badge = (
        <Chip
            label={`${label}: ${value}`}
            size="small"
            onClick={handleClick}
            sx={{
                fontSize: '0.75rem',
                height: '24px',
                backgroundColor: color,
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-label': {
                    px: 1.5,
                },
                cursor: onClick ? 'pointer' : (tooltip ? 'help' : 'default'),
                '&:hover': onClick ? {
                    backgroundColor: color,
                    opacity: 0.85,
                    transform: 'scale(1.05)',
                } : {},
                transition: 'all 0.2s',
            }}
        />
    );

    if (tooltip) {
        return (
            <Box component="span" sx={{ display: 'inline-block' }}>
                <Tooltip title={`${tooltip}${onClick ? ' (Click for details)' : ''}`} arrow placement="top">
                    {badge}
                </Tooltip>
            </Box>
        );
    }

    return badge;
};

// Render feature badges in a compact grid
const renderFeatureBadges = (params: any, onFeatureClick?: (featureName: string, featureKey: string, color: string) => void) => {
    const usage = params.row.featureUsage;
    const features = [
        {
            label: 'Transcriptions',
            key: 'transcriptions',
            value: usage.transcriptions,
            color: '#1976d2',
            tooltip: 'Number of audio/video transcriptions created'
        },
        {
            label: 'Transcript Duration',
            key: 'transcriptionDurationMinutes',
            // Convert minutes to seconds for the formatDuration helper
            value: usage.transcriptionDurationMinutes ? formatDuration(usage.transcriptionDurationMinutes * 60) : 0,
            color: '#0288d1',
            tooltip: 'Total duration of transcribed content'
        },
        {
            label: 'Fact Finds',
            key: 'factFinds',
            value: usage.factFinds,
            color: '#2e7d32',
            tooltip: 'Number of fact find forms completed'
        },
        {
            label: 'Meetings',
            key: 'meetings',
            value: usage.meetings,
            color: '#ed6c02',
            tooltip: 'Number of meetings scheduled or created'
        },
        {
            label: 'Clients Created',
            key: 'clients',
            value: usage.clients,
            color: '#9c27b0',
            tooltip: 'Number of new client records created'
        },
        {
            label: 'Clients Assigned',
            key: 'clientsAssigned',
            value: usage.clientsAssigned,
            color: '#7b1fa2',
            tooltip: 'Number of clients assigned to this advisor'
        },
        {
            label: 'Documents',
            key: 'documents',
            value: usage.documents,
            color: '#0288d1',
            tooltip: 'Number of documents uploaded'
        },
        {
            label: 'AI Agent',
            key: 'aiAgentUsage',
            value: usage.aiAgentUsage,
            color: '#d32f2f',
            tooltip: 'Number of AI agent interactions'
        },
        {
            label: 'Contacts',
            key: 'contacts',
            value: usage.contacts,
            color: '#5e35b1',
            tooltip: 'Number of contact records created'
        },
        {
            label: 'File Notes',
            key: 'fileNotesGenerated',
            value: usage.fileNotesGenerated,
            color: '#388e3c',
            tooltip: 'Number of file notes generated'
        },
        {
            label: 'Note Regens',
            key: 'fileNotesRegenerated',
            value: usage.fileNotesRegenerated,
            color: '#689f38',
            tooltip: 'Number of file notes regenerated'
        },
        {
            label: 'PowerPoints',
            key: 'powerPointsGenerated',
            value: usage.powerPointsGenerated,
            color: '#f57c00',
            tooltip: 'Number of PowerPoint presentations generated'
        },
        {
            label: 'Emails Generated',
            key: 'emailsGenerated',
            value: usage.emailsGenerated,
            color: '#5e35b1',
            tooltip: 'Number of emails generated by AI'
        },
        {
            label: 'Emails Sent',
            key: 'emailsSent',
            value: usage.emailsSent,
            color: '#1565c0',
            tooltip: 'Number of emails actually sent'
        },
        {
            label: 'SMS Sent',
            key: 'smsSent',
            value: usage.smsSent,
            color: '#ff5722',
            tooltip: 'Number of SMS messages sent'
        },
        {
            label: 'SMS Segments',
            key: 'smsSegments',
            value: usage.smsSegments,
            color: '#e64a19',
            tooltip: 'Total SMS segments used (for billing purposes)'
        },
    ];

    const usedFeatures = features.filter(f => f.value && f.value !== 0 && f.value !== '0m');

    if (usedFeatures.length === 0) {
        return <Typography variant="caption" color="text.secondary" fontStyle="italic">No activity</Typography>;
    }

    return (
        <Box display="flex" flexWrap="wrap" gap={0.75} py={1}>
            {usedFeatures.map((feature, index) => (
                <FeatureBadge
                    key={index}
                    label={feature.label}
                    value={feature.value}
                    color={feature.color}
                    tooltip={feature.tooltip}
                    onClick={onFeatureClick ? () => onFeatureClick(feature.label, feature.key, feature.color) : undefined}
                />
            ))}
        </Box>
    );
};

const UserSessionDetailsModal = ({ open, onClose, userId, userName, orgId }: UserSessionDetailsModalProps) => {
    const [sessions, setSessions] = useState<SessionData[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
    const [tabValue, setTabValue] = useState(0);
    const [featureFilter, setFeatureFilter] = useState<string>('all');
    const [selectedFeatures, setSelectedFeatures] = useState<Array<{ name: string; key: string; color: string }>>([]);
    const [viewMode, setViewMode] = useState<'sessions' | 'featureAnalysis'>('sessions');
    const [dateRange, setDateRange] = useState<'7d' | '14d' | '30d' | 'all'>('all');

    useEffect(() => {
        const fetchSessionDetails = async () => {
            if (!userId || !open) return;

            setLoading(true);

            try {
                // Build query with orgId parameter for backend filtering
                const params = new URLSearchParams();
                if (orgId) {
                    params.append('orgId', orgId);
                }
                const queryString = params.toString();

                const response: any = await fetchUserSessionDetails(userId, queryString);
                const filteredSessions = response.sessions || [];

                setSessions(filteredSessions);
            } catch (err) {
                console.error('Failed to fetch session details:', err);
                setSessions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSessionDetails();
    }, [userId, open, orgId]);

    const handleFeatureClick = (featureName: string, featureKey: string, color: string) => {
        setSelectedFeatures([{ name: featureName, key: featureKey, color }]);
        setViewMode('featureAnalysis');
    };

    const handleBackToSessions = () => {
        setViewMode('sessions');
        setSelectedFeatures([]);
    };

    const sessionColumns: GridColDef[] = [
        {
            field: 'startTime',
            headerName: 'SESSION START',
            width: 180,
            renderCell: (params) => dayjs(params.value).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            field: 'endTime',
            headerName: 'SESSION END',
            width: 150,
            renderCell: (params) => params.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm:ss') : (
                <Chip label="Active" size="small" color="success" />
            ),
        },
        {
            field: 'duration',
            headerName: 'DURATION',
            width: 110,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => formatDuration(params.value),
        },
        {
            field: 'featureUsage',
            headerName: 'FEATURE USAGE',
            flex: 1,
            minWidth: 400,
            disableColumnMenu: true,
            sortable: false,
            filterable: false,
            renderCell: (params) => renderFeatureBadges(params, handleFeatureClick),
        },
    ];

    // Map action names to feature keys and colors
    const getActionFeatureMapping = (actionName: string): { key: string; color: string; label: string } | null => {
        const actionLower = actionName.toLowerCase().replace(/_/g, ' ');

        // AI Agent
        if (actionLower.includes('ai agent') || actionLower.includes('ai chat')) {
            return { key: 'aiAgentUsage', color: '#d32f2f', label: 'AI Agent' };
        }

        // Transcriptions
        if (actionLower.includes('transcription') || actionLower.includes('transcribe')) {
            return { key: 'transcriptions', color: '#1976d2', label: 'Transcription' };
        }

        // Fact Finds
        if (actionLower.includes('fact find') || actionLower.includes('factfind')) {
            return { key: 'factFinds', color: '#2e7d32', label: 'Fact Find' };
        }

        // Meetings
        if (actionLower.includes('meeting')) {
            return { key: 'meetings', color: '#ed6c02', label: 'Meeting' };
        }

        // Clients
        if (actionLower.includes('client') && actionLower.includes('assign')) {
            return { key: 'clientsAssigned', color: '#7b1fa2', label: 'Client Assigned' };
        }
        if (actionLower.includes('client') && (actionLower.includes('create') || actionLower.includes('add') || actionLower.includes('new'))) {
            return { key: 'clients', color: '#9c27b0', label: 'Client Created' };
        }
        if (actionLower.includes('client')) {
            return { key: 'clients', color: '#9c27b0', label: 'Client' };
        }

        // Documents
        if (actionLower.includes('document') || actionLower.includes('file upload')) {
            return { key: 'documents', color: '#0288d1', label: 'Document' };
        }

        // Contacts
        if (actionLower.includes('contact')) {
            return { key: 'contacts', color: '#5e35b1', label: 'Contact' };
        }

        // File Notes
        if (actionLower.includes('file note') && actionLower.includes('regen')) {
            return { key: 'fileNotesRegenerated', color: '#689f38', label: 'Note Regen' };
        }
        if (actionLower.includes('file note') || actionLower.includes('note generat')) {
            return { key: 'fileNotesGenerated', color: '#388e3c', label: 'File Note' };
        }

        // PowerPoints
        if (actionLower.includes('powerpoint') || actionLower.includes('presentation') || actionLower.includes('ppt')) {
            return { key: 'powerPointsGenerated', color: '#f57c00', label: 'PowerPoint' };
        }

        // Emails
        if (actionLower.includes('email') && actionLower.includes('sent')) {
            return { key: 'emailsSent', color: '#1565c0', label: 'Email Sent' };
        }
        if (actionLower.includes('email') && actionLower.includes('generat')) {
            return { key: 'emailsGenerated', color: '#5e35b1', label: 'Email Generated' };
        }
        if (actionLower.includes('email')) {
            return { key: 'emailsGenerated', color: '#5e35b1', label: 'Email' };
        }

        // SMS
        if (actionLower.includes('sms') && actionLower.includes('sent')) {
            return { key: 'smsSent', color: '#ff5722', label: 'SMS Sent' };
        }
        if (actionLower.includes('sms')) {
            return { key: 'smsSent', color: '#ff5722', label: 'SMS' };
        }

        return null;
    };

    const timelineColumns: GridColDef[] = [
        {
            field: 'timestamp',
            headerName: 'TIMESTAMP',
            width: 200,
            renderCell: (params) => dayjs(params.value).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            field: 'actionName',
            headerName: 'ACTION',
            flex: 1,
            renderCell: (params) => {
                const actionName = params.value as string;
                const featureMapping = getActionFeatureMapping(actionName);

                if (featureMapping) {
                    return (
                        <Chip
                            label={actionName}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleFeatureClick(featureMapping.label, featureMapping.key, featureMapping.color);
                            }}
                            sx={{
                                fontSize: '0.75rem',
                                height: '24px',
                                backgroundColor: featureMapping.color,
                                color: 'white',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: featureMapping.color,
                                    opacity: 0.85,
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s',
                            }}
                        />
                    );
                }

                // Default badge for unmapped actions
                return (
                    <Chip
                        label={actionName}
                        size="small"
                        sx={{
                            fontSize: '0.75rem',
                            height: '24px',
                            backgroundColor: '#757575',
                            color: 'white',
                            fontWeight: 500,
                        }}
                    />
                );
            },
        },
    ];

    // Filter sessions based on feature usage
    const filteredSessions = sessions.filter(session => {
        if (featureFilter === 'all') return true;

        const usage = session.featureUsage;
        switch (featureFilter) {
            case 'transcriptions':
                return usage.transcriptions > 0;
            case 'factFinds':
                return usage.factFinds > 0;
            case 'meetings':
                return usage.meetings > 0;
            case 'clients':
                return usage.clients > 0;
            case 'documents':
                return usage.documents > 0;
            case 'aiAgentUsage':
                return usage.aiAgentUsage > 0;
            case 'contacts':
                return usage.contacts > 0;
            case 'fileNotes':
                return usage.fileNotesGenerated > 0;
            case 'powerPoints':
                return usage.powerPointsGenerated > 0;
            case 'emails':
                return usage.emailsGenerated > 0 || usage.emailsSent > 0;
            case 'sms':
                return usage.smsSent > 0 || usage.smsSegments > 0;
            default:
                return true;
        }
    });

    // Calculate most productive sessions (top 10 by total feature usage)
    const productiveSessions = [...sessions]
        .map(session => {
            const usage = session.featureUsage;
            const totalUsage =
                usage.transcriptions +
                usage.factFinds +
                usage.meetings +
                usage.clients +
                usage.documents +
                usage.aiAgentUsage +
                usage.contacts +
                usage.fileNotesGenerated +
                usage.powerPointsGenerated +
                usage.emailsGenerated +
                usage.smsSent +
                usage.smsSegments;
            return { ...session, totalUsage };
        })
        .sort((a, b) => b.totalUsage - a.totalUsage)
        .slice(0, 10);

    const sessionsWithId = filteredSessions.map((session, index) => ({
        ...session,
        id: session.sessionId || `session-${index}`,
    }));

    const timelineWithId = selectedSession?.timeline.map((activity, index) => ({
        ...activity,
        id: `activity-${index}`,
    })) || [];

    // Calculate multi-feature analysis data
    const getMultiFeatureAnalysis = () => {
        if (selectedFeatures.length === 0) return null;

        // Filter sessions by date range
        const now = dayjs();
        let filteredByDate = sessions;

        if (dateRange !== 'all') {
            const days = parseInt(dateRange);
            const cutoffDate = now.subtract(days, 'day');
            filteredByDate = sessions.filter(session =>
                dayjs(session.startTime).isAfter(cutoffDate)
            );
        }

        // Get all unique dates
        const allDates = new Set<string>();
        filteredByDate.forEach(session => {
            allDates.add(dayjs(session.startTime).format('YYYY-MM-DD'));
        });

        // Build chart data with all features
        const chartData: any[] = [];
        const sortedDates = Array.from(allDates).sort((a, b) =>
            dayjs(a).valueOf() - dayjs(b).valueOf()
        );

        sortedDates.forEach(date => {
            const dataPoint: any = { date };

            selectedFeatures.forEach(feature => {
                const sessionsOnDate = filteredByDate.filter(session =>
                    dayjs(session.startTime).format('YYYY-MM-DD') === date
                );

                const totalUsage = sessionsOnDate.reduce((sum, session) => {
                    const value = (session.featureUsage as any)[feature.key];
                    return sum + (typeof value === 'number' ? value : 0);
                }, 0);

                dataPoint[feature.key] = totalUsage;
            });

            chartData.push(dataPoint);
        });

        // Calculate summary stats for each feature
        const featureStats = selectedFeatures.map(feature => {
            const sessionsWithFeature = filteredByDate.filter(session => {
                const value = (session.featureUsage as any)[feature.key];
                return value && value > 0;
            });

            const totalUsage = sessionsWithFeature.reduce((sum, session) => {
                const value = (session.featureUsage as any)[feature.key];
                return sum + (typeof value === 'number' ? value : 0);
            }, 0);

            const avgUsagePerSession = sessionsWithFeature.length > 0
                ? (totalUsage / sessionsWithFeature.length).toFixed(2)
                : 0;

            const mostProductiveSession = sessionsWithFeature.length > 0
                ? sessionsWithFeature.reduce((max, session) => {
                    const value = (session.featureUsage as any)[feature.key];
                    const maxValue = (max.featureUsage as any)[feature.key];
                    return value > maxValue ? session : max;
                })
                : null;

            return {
                feature,
                totalUsage,
                sessionsWithFeature: sessionsWithFeature.length,
                totalSessions: filteredByDate.length,
                avgUsagePerSession,
                mostProductiveSession,
                usagePercentage: filteredByDate.length > 0
                    ? ((sessionsWithFeature.length / filteredByDate.length) * 100).toFixed(1)
                    : 0,
            };
        });

        return {
            chartData,
            featureStats,
        };
    };

    const multiFeatureAnalysis = selectedFeatures.length > 0 ? getMultiFeatureAnalysis() : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {viewMode === 'featureAnalysis' && selectedFeatures.length > 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconButton onClick={handleBackToSessions} size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h6" fontWeight="bold">
                                Feature Analysis {userName && `- ${userName}`}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="h6" fontWeight="bold">
                            Session Details {userName && `- ${userName}`}
                        </Typography>
                    )}
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                        <CircularProgress />
                    </Box>
                ) : viewMode === 'featureAnalysis' && multiFeatureAnalysis ? (
                    /* Feature Analysis View */
                    <Box>
                        {/* User Info Banner */}
                        {userName && (
                            <Box mb={2} p={2} sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Analyzing feature usage for: <strong>{userName}</strong>
                                </Typography>
                            </Box>
                        )}

                        {/* Feature Selection */}
                        <Card sx={{ mb: 3, boxShadow: 1 }}>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                                    Select Features to Compare (Click to add/remove)
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {[
                                        { label: 'Transcriptions', key: 'transcriptions', color: '#1976d2' },
                                        { label: 'Transcript Duration', key: 'transcriptionDurationMinutes', color: '#0288d1' },
                                        { label: 'Fact Finds', key: 'factFinds', color: '#2e7d32' },
                                        { label: 'Meetings', key: 'meetings', color: '#ed6c02' },
                                        { label: 'Clients Created', key: 'clients', color: '#9c27b0' },
                                        { label: 'Clients Assigned', key: 'clientsAssigned', color: '#7b1fa2' },
                                        { label: 'Documents', key: 'documents', color: '#0288d1' },
                                        { label: 'AI Agent', key: 'aiAgentUsage', color: '#d32f2f' },
                                        { label: 'Contacts', key: 'contacts', color: '#5e35b1' },
                                        { label: 'File Notes', key: 'fileNotesGenerated', color: '#388e3c' },
                                        { label: 'Note Regens', key: 'fileNotesRegenerated', color: '#689f38' },
                                        { label: 'PowerPoints', key: 'powerPointsGenerated', color: '#f57c00' },
                                        { label: 'Emails Generated', key: 'emailsGenerated', color: '#5e35b1' },
                                        { label: 'Emails Sent', key: 'emailsSent', color: '#1565c0' },
                                        { label: 'SMS Sent', key: 'smsSent', color: '#ff5722' },
                                        { label: 'SMS Segments', key: 'smsSegments', color: '#e64a19' },
                                    ].map((feature) => {
                                        const isSelected = selectedFeatures.some(f => f.key === feature.key);
                                        return (
                                            <Chip
                                                key={feature.key}
                                                label={feature.label}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedFeatures(prev => prev.filter(f => f.key !== feature.key));
                                                    } else {
                                                        setSelectedFeatures(prev => [...prev, { name: feature.label, key: feature.key, color: feature.color }]);
                                                    }
                                                }}
                                                sx={{
                                                    backgroundColor: isSelected ? feature.color : '#f5f5f5',
                                                    color: isSelected ? 'white' : 'text.primary',
                                                    fontWeight: isSelected ? 600 : 400,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: isSelected ? feature.color : '#e0e0e0',
                                                        opacity: isSelected ? 0.9 : 1,
                                                    },
                                                    transition: 'all 0.2s',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Date Range Filter */}
                        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={1}>
                                <TrendingUpIcon color="primary" />
                                <Typography variant="body1" fontWeight="600">
                                    Showing data for: <strong>{dateRange === 'all' ? 'All Time' : `Last ${dateRange.replace('d', ' Days')}`}</strong>
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                value={dateRange}
                                exclusive
                                onChange={(_, newValue) => newValue && setDateRange(newValue)}
                                size="small"
                            >
                                <ToggleButton value="7d">Last 7 Days</ToggleButton>
                                <ToggleButton value="14d">Last 14 Days</ToggleButton>
                                <ToggleButton value="30d">Last 30 Days</ToggleButton>
                                <ToggleButton value="all">All Time</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {/* No Features Selected Message */}
                        {selectedFeatures.length === 0 && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                minHeight={300}
                                sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '2px dashed #cbd5e1', p: 4 }}
                            >
                                <BarChartIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" mb={1}>
                                    No Features Selected
                                </Typography>
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    Select one or more features above to view usage trends and comparisons
                                </Typography>
                            </Box>
                        )}

                        {/* Summary Cards - Compact Design */}
                        {selectedFeatures.length > 0 && (
                            <Grid container spacing={2} mb={3}>
                                {multiFeatureAnalysis.featureStats.map((stat, index) => {
                                    const isTimeMetric = stat.feature.key === 'transcriptionDurationMinutes';

                                    return (
                                        <Grid item xs={12} sm={6} md={selectedFeatures.length === 1 ? 3 : selectedFeatures.length === 2 ? 6 : 4} key={index}>
                                            <Card sx={{ boxShadow: 2, border: `2px solid ${stat.feature.color}` }}>
                                                <CardContent>
                                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: stat.feature.color }} />
                                                        <Typography variant="body2" fontWeight="bold" color={stat.feature.color}>
                                                            {stat.feature.name}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="h5" fontWeight="bold" color={stat.feature.color} mb={1}>
                                                        {isTimeMetric
                                                            ? formatDuration(stat.totalUsage * 60)
                                                            : stat.totalUsage.toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
                                                        Used in {stat.sessionsWithFeature} of {stat.totalSessions} sessions ({stat.usagePercentage}%)
                                                    </Typography>
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        Average: <strong>
                                                            {isTimeMetric
                                                                ? formatDuration(parseFloat(stat.avgUsagePerSession as string) * 60)
                                                                : stat.avgUsagePerSession}
                                                        </strong> per active session
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}

                        {/* Usage Trend Chart */}
                        <Card sx={{ mb: 3, boxShadow: 1 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <BarChartIcon color="primary" />
                                        <Typography variant="h6" fontWeight="bold">
                                            Usage Trend Over Time
                                        </Typography>
                                    </Box>
                                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                            {multiFeatureAnalysis.chartData.length} days tracked
                                        </Typography>
                                    </Box>
                                </Box>
                                <ResponsiveContainer width="100%" height={400}>
                                    <ComposedChart
                                        data={multiFeatureAnalysis.chartData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(date) => dayjs(date).format('MMM DD')}
                                            tick={{ fontSize: 11 }}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11 }}
                                            label={{ value: 'Usage Count', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                                        />
                                        <RechartsTooltip
                                            labelFormatter={(date) => dayjs(date).format('MMMM D, YYYY')}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                padding: '12px'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ paddingTop: '20px' }}
                                            iconType="rect"
                                        />
                                        {selectedFeatures.map((feature) => (
                                            <Bar
                                                key={feature.key}
                                                dataKey={feature.key}
                                                fill={feature.color}
                                                name={feature.name}
                                                radius={[4, 4, 0, 0]}
                                            />
                                        ))}
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        {/* Most Productive Sessions */}
                        {multiFeatureAnalysis.featureStats.some(stat => stat.mostProductiveSession) && (
                            <Card sx={{ mb: 3, boxShadow: 1 }}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                                        <EmojiEventsIcon color="warning" />
                                        <Typography variant="h6" fontWeight="bold">
                                            Most Productive Sessions
                                        </Typography>
                                    </Box>
                                    {multiFeatureAnalysis.featureStats.map((stat) => {
                                        if (!stat.mostProductiveSession) return null;

                                        const isTimeMetric = stat.feature.key === 'transcriptionDurationMinutes';
                                        const rawValue = (stat.mostProductiveSession.featureUsage as any)[stat.feature.key];

                                        return (
                                            <Box key={stat.feature.key} sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1} color={stat.feature.color}>
                                                    {stat.feature.name}
                                                </Typography>
                                                <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>{dayjs(stat.mostProductiveSession.startTime).format('MMM D, YYYY HH:mm')}</strong>
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <TrackChangesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>
                                                                {isTimeMetric
                                                                    ? formatDuration(rawValue * 60)
                                                                    : `${rawValue} uses`}
                                                            </strong>
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Session Duration: <strong>{formatDuration(stat.mostProductiveSession.duration)}</strong>
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        )}

                        {/* Comparison Insights */}
                        {selectedFeatures.length > 1 && (
                            <Card sx={{ boxShadow: 1 }}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                                        <LightbulbIcon color="primary" />
                                        <Typography variant="h6" fontWeight="bold">
                                            Feature Comparison Insights
                                        </Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #90caf9' }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
                                                    Most Used Feature
                                                </Typography>
                                                {(() => {
                                                    const mostUsed = multiFeatureAnalysis.featureStats.reduce((max, stat) =>
                                                        stat.totalUsage > max.totalUsage ? stat : max
                                                    );
                                                    const isTimeMetric = mostUsed.feature.key === 'transcriptionDurationMinutes';
                                                    return (
                                                        <>
                                                            <Typography variant="body2" color={mostUsed.feature.color} fontWeight="bold">
                                                                {mostUsed.feature.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {isTimeMetric
                                                                    ? `${formatDuration(mostUsed.totalUsage * 60)} total duration`
                                                                    : `${mostUsed.totalUsage.toLocaleString()} total uses`}
                                                            </Typography>
                                                        </>
                                                    );
                                                })()}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2, border: '1px solid #ce93d8' }}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="secondary" mb={1}>
                                                    Highest Coverage
                                                </Typography>
                                                {(() => {
                                                    const highestCoverage = multiFeatureAnalysis.featureStats.reduce((max, stat) =>
                                                        parseFloat(stat.usagePercentage as string) > parseFloat(max.usagePercentage as string) ? stat : max
                                                    );
                                                    return (
                                                        <>
                                                            <Typography variant="body2" color={highestCoverage.feature.color} fontWeight="bold">
                                                                {highestCoverage.feature.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Used in {highestCoverage.usagePercentage}% of sessions
                                                            </Typography>
                                                        </>
                                                    );
                                                })()}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                ) : (
                    /* Session History View */
                    <>
                        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                            <Tab label="Session History" />
                            {selectedSession && <Tab label="Activity Timeline" />}
                        </Tabs>

                        {tabValue === 0 && (
                            <Box>
                                {/* User Info Banner */}
                                {userName && (
                                    <Box mb={2} p={2} sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Viewing session history for: <strong>{userName}</strong>
                                        </Typography>
                                    </Box>
                                )}

                                <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {filteredSessions.length} of {sessions.length} sessions
                                    </Typography>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <InputLabel>Filter by Feature</InputLabel>
                                        <Select
                                            value={featureFilter}
                                            label="Filter by Feature"
                                            onChange={(e: SelectChangeEvent) => setFeatureFilter(e.target.value)}
                                        >
                                            <MenuItem value="all">All Sessions</MenuItem>
                                            <MenuItem value="transcriptions">Transcriptions</MenuItem>
                                            <MenuItem value="factFinds">Fact Finds</MenuItem>
                                            <MenuItem value="meetings">Meetings</MenuItem>
                                            <MenuItem value="clients">Clients</MenuItem>
                                            <MenuItem value="documents">Documents</MenuItem>
                                            <MenuItem value="aiAgentUsage">AI Agent Usage</MenuItem>
                                            <MenuItem value="contacts">Contacts</MenuItem>
                                            <MenuItem value="fileNotes">File Notes</MenuItem>
                                            <MenuItem value="powerPoints">PowerPoints</MenuItem>
                                            <MenuItem value="emails">Emails</MenuItem>
                                            <MenuItem value="sms">SMS</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Most Productive Sessions Section */}
                                {featureFilter === 'all' && productiveSessions.length > 0 && (
                                    <Box mb={3} p={2} sx={{ bgcolor: '#F5F5F5', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                            Most Productive Sessions
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {productiveSessions.slice(0, 5).map((session, index) => (
                                                <Chip
                                                    key={session.sessionId}
                                                    label={`#${index + 1}: ${dayjs(session.startTime).format('MMM DD, HH:mm')} (${(session as any).totalUsage} actions)`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setSelectedSession(session);
                                                        setTabValue(1);
                                                    }}
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={{ height: 500, width: '100%' }}>
                                    <DataGrid
                                        rows={sessionsWithId}
                                        columns={sessionColumns}
                                        disableRowSelectionOnClick={false}
                                        onRowClick={(params) => {
                                            setSelectedSession(params.row as SessionData);
                                            setTabValue(1);
                                        }}
                                        getRowHeight={() => 'auto'}
                                        slots={{
                                            noRowsOverlay: CustomNoRowsOverlay,
                                        }}
                                        sx={{
                                            '& .MuiDataGrid-cell': {
                                                py: 1,
                                            },
                                            '& .MuiDataGrid-row': {
                                                cursor: 'pointer',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {tabValue === 1 && selectedSession && (
                            <Box>
                                <Box mb={2}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Session: {dayjs(selectedSession.startTime).format('YYYY-MM-DD HH:mm:ss')}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                                        <Chip
                                            label={`Duration: ${formatDuration(selectedSession.duration)}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={selectedSession.isActive ? 'Active' : 'Ended'}
                                            size="small"
                                            color={selectedSession.isActive ? 'success' : 'default'}
                                        />
                                        {selectedSession.endReason && (
                                            <Chip
                                                label={`End Reason: ${selectedSession.endReason}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Activity Timeline ({selectedSession.timeline.length} activities)
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 450, width: '100%' }}>
                                    <DataGrid
                                        rows={timelineWithId}
                                        columns={timelineColumns}
                                        disableRowSelectionOnClick
                                        getRowHeight={() => 'auto'}
                                        slots={{
                                            noRowsOverlay: () => (
                                                <NoDataFound
                                                    title="No activities"
                                                    subtitle="No activities recorded for this session."
                                                    imageWidth={100}
                                                    imageHeight={100}
                                                />
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiDataGrid-cell': {
                                                py: 1,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UserSessionDetailsModal;
